import React, { useState } from "react";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import Input from "../../../components/ui/Input";
import Icon from "../../../components/AppIcon";

const ConsultationBooking = () => {
  const [selectedService, setSelectedService] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const consultationTypes = [
    {
      value: "wedding",
      label: "Romantic Wedding Consultation",
      description: "Discuss your love story and vision",
      duration: "60 minutes",
      icon: "Heart",
      color: "text-pink-600",
    },
    {
      value: "realestate",
      label: "Strategic Real Estate Session",
      description: "Property marketing strategy",
      duration: "45 minutes",
      icon: "Home",
      color: "text-blue-600",
    },
    {
      value: "marketing",
      label: "Creative Brand Storytelling",
      description: "Brand campaign development",
      duration: "90 minutes",
      icon: "Megaphone",
      color: "text-orange-600",
    },
    {
      value: "design",
      label: "Visionary Design Fiction",
      description: "Explore conceptual possibilities",
      duration: "75 minutes",
      icon: "Zap",
      color: "text-purple-600",
    },
  ];

  const availableDates = [
    { value: "2025-01-22", label: "Wednesday, January 22" },
    { value: "2025-01-23", label: "Thursday, January 23" },
    { value: "2025-01-24", label: "Friday, January 24" },
    { value: "2025-01-27", label: "Monday, January 27" },
    { value: "2025-01-28", label: "Tuesday, January 28" },
    { value: "2025-01-29", label: "Wednesday, January 29" },
  ];

  const availableTimes = [
    { value: "09:00", label: "9:00 AM EST" },
    { value: "10:30", label: "10:30 AM EST" },
    { value: "12:00", label: "12:00 PM EST" },
    { value: "13:30", label: "1:30 PM EST" },
    { value: "15:00", label: "3:00 PM EST" },
    { value: "16:30", label: "4:30 PM EST" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log("Booking consultation:", {
      service: selectedService,
      date: selectedDate,
      time: selectedTime,
      ...formData,
    });
    // Handle form submission
  };

  const selectedConsultationType = consultationTypes?.find(
    (type) => type?.value === selectedService
  );

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-fluid-3xl font-display font-bold text-primary mb-4">
              Book Your Free Consultation
            </h2>
            <p className="text-fluid-lg text-muted-foreground max-w-2xl mx-auto">
              Schedule a personalized session to discuss your project vision and
              explore how we can bring it to life.
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Consultation Types */}
              <div className="p-8 border-b lg:border-b-0 lg:border-r border-border">
                <h3 className="text-xl font-semibold text-card-foreground mb-6">
                  Choose Your Consultation Type
                </h3>

                <div className="space-y-4">
                  {consultationTypes?.map((type) => (
                    <div
                      key={type?.value}
                      className={`p-4 rounded-lg border cursor-pointer transition-smooth ${
                        selectedService === type?.value
                          ? "border-accent bg-accent/10"
                          : "border-border hover:border-accent/50"
                      }`}
                      onClick={() => setSelectedService(type?.value)}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className={`flex-shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center ${type?.color}`}
                        >
                          <Icon name={type?.icon} size={16} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-card-foreground mb-1">
                            {type?.label}
                          </h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            {type?.description}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                            <span className="flex items-center space-x-1">
                              <Icon name="Clock" size={12} />
                              <span>{type?.duration}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Icon name="DollarSign" size={12} />
                              <span>Free</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedConsultationType && (
                  <div className="mt-6 p-4 bg-accent/5 rounded-lg">
                    <h4 className="font-medium text-card-foreground mb-2">
                      What to Expect:
                    </h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Portfolio review and style discussion</li>
                      <li>• Project timeline and budget planning</li>
                      <li>• Custom proposal development</li>
                      <li>• Q&A session with our experts</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Booking Form */}
              <div className="p-8">
                <h3 className="text-xl font-semibold text-card-foreground mb-6">
                  Schedule Your Session
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Select
                      label="Preferred Date"
                      placeholder="Select date"
                      options={availableDates}
                      value={selectedDate}
                      onChange={setSelectedDate}
                      required
                    />

                    <Select
                      label="Preferred Time"
                      placeholder="Select time"
                      options={availableTimes}
                      value={selectedTime}
                      onChange={setSelectedTime}
                      required
                      disabled={!selectedDate}
                    />
                  </div>

                  <Input
                    label="Full Name"
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    required
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Email Address"
                      type="email"
                      name="email"
                      placeholder="your@email.com"
                      value={formData?.email}
                      onChange={handleInputChange}
                      required
                    />

                    <Input
                      label="Phone Number"
                      type="tel"
                      name="phone"
                      placeholder="(555) 123-4567"
                      value={formData?.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Project Details
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      className="w-full px-3 py-2 border border-border rounded-md text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      placeholder="Tell us about your project vision, timeline, and any specific requirements..."
                      value={formData?.message}
                      onChange={handleInputChange}
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    className="bg-cta hover:bg-cta/90 text-cta-foreground"
                    iconName="Calendar"
                    iconPosition="left"
                    disabled={
                      !selectedService || !selectedDate || !selectedTime
                    }
                  >
                    Book Free Consultation
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    By booking, you agree to our terms of service. We'll send
                    you a calendar invite with meeting details.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationBooking;
